import { cloneDeep, isEqual } from "lodash";
import { createPatch, Patch } from "rfc6902";
import { isEditableMetadataPath } from "./isEditableMetadataPath";
import { omitStaticMetadata } from "./omitStaticMetadata";
import { toEditableData } from "./toEditableData";

/**
 * Builds isolated reversible operations while retaining complete added and removed entity values.
 *
 * @param before - Complete selected domain objects before the action.
 * @param after - Complete selected domain objects after the action.
 * @returns Full-value patches excluding operations targeting ignored metadata fields.
 */
export const editablePatch = (
  before: ReturnType<typeof toEditableData>,
  after: ReturnType<typeof toEditableData>,
): Patch => {
  const originals = new Map<unknown, unknown>();
  const strippedBefore = omitStaticMetadata(before, [], undefined, originals);
  const strippedAfter = omitStaticMetadata(after, [], undefined, originals);
  if (isEqual(strippedBefore, strippedAfter)) return [];
  // Diff without ignored metadata: rfc6902 recursion depth grows with array length, so diffing
  // large metadata such as clinical libraries overflows the stack. If a subtree is already
  // equal, short-circuit before we recurse into it and lose the reference-equality optimisation.
  // Operation values are mapped back to their complete domain objects.
  return cloneDeep(
    createPatch(before, after, (_input, _output, ptr) =>
      isEditableMetadataPath(ptr.tokens.slice(1)) ? [] : undefined,
    ).filter(
      (operation) =>
        !isEditableMetadataPath(
          operation.path
            .split("/")
            .slice(1)
            .map((segment) => segment.replace(/~1/g, "/").replace(/~0/g, "~")),
        ),
    ),
  );
};
